import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useEffect, useState } from "react";

import { Site } from "../../../../../dtos/site";
import { AjaxResult } from "../../../../../enums/ajaxResult";
import { RequestState } from "../../../../../enums/requestState";
import SignalRContext from "../../../../../signalr/context";
import { ButtonWithConfirm } from "../../../../ButtonWithConfirm";
import { useApi } from "../../../../useApi";

import { actionButtonStyle } from "./actionButtonStyle";

const buttonLabels: { [key in RequestState]: string; } = {
    [RequestState.Idle]: "Restart",
    [RequestState.Requesting]: "Requesting...",
    [RequestState.Processing]: "Restarting System...",
}

interface Props {
    site: Site;
}

export function SystemRestartButton({ site }: Props) {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { systemRestartTriggered } = useContext(SignalRContext);

    const [requestState, setRequestState] = useState<RequestState>(RequestState.Idle);

    const handleSystemRestart = useCallback(() => {
        setRequestState(RequestState.Requesting);
        siteApi.restartSystem(site.id).then((r) => {
            if (r.result !== AjaxResult.Success) {
                setRequestState(RequestState.Idle);
                enqueueSnackbar("ERROR: Failed to request system restart", { variant: "error" });
            } else {
                setRequestState(RequestState.Processing);
            }
        });
    }, [siteApi, site.id, enqueueSnackbar]);

    useEffect(() => {
        if (requestState === RequestState.Processing && systemRestartTriggered === site.id) {
            setRequestState(RequestState.Idle);
            enqueueSnackbar("System restart is in progress", { variant: "success" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [systemRestartTriggered]);

    return (
        <ButtonWithConfirm
            confirmDialogContent="Are you sure to restart the system?"
            confirmedCallback={handleSystemRestart}
            disabled={requestState !== RequestState.Idle}
            style={actionButtonStyle}
        >
            {buttonLabels[requestState]}
        </ButtonWithConfirm>
    );
}